import React from 'react';
import './TermsOfService.css';

export const TermsOfService: React.FC = () => {
  return (
    <div className="terms-of-service">
      <div className="terms-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: January 23, 2025</p>

        <section className="terms-section">
          <h2>1. Agreement to Terms</h2>
          <p>
            By accessing or using BigSystemOne ("the Service"), you agree to be bound by these Terms of Service 
            ("Terms"). If you disagree with any part of these terms, then you may not access the Service.
          </p>
          <p>
            These Terms apply to all visitors, users, and others who access or use the Service, including but not 
            limited to restaurant owners, managers, employees, and any authorized users of the BigSystemOne platform.
          </p>
        </section>

        <section className="terms-section">
          <h2>2. Description of Service</h2>
          <p>
            BigSystemOne provides a comprehensive restaurant management platform that includes:
          </p>
          <ul>
            <li>Point of Sale (POS) systems and management</li>
            <li>Employee management and scheduling</li>
            <li>Inventory tracking and management</li>
            <li>Menu management and pricing</li>
            <li>Customer relationship management</li>
            <li>Analytics and reporting</li>
            <li>Social media integration and marketing tools</li>
            <li>Mobile applications for various restaurant operations</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>3. Account Registration and Security</h2>
          <h3>3.1 Account Creation</h3>
          <p>
            To use certain features of the Service, you must register for an account. You agree to:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain and promptly update your account information</li>
            <li>Maintain the security of your password and accept all risks of unauthorized access</li>
            <li>Notify us immediately if you discover or suspect any security breaches</li>
          </ul>

          <h3>3.2 Account Responsibility</h3>
          <p>
            You are responsible for all activities that occur under your account. You may not:
          </p>
          <ul>
            <li>Share your account credentials with others</li>
            <li>Use another person's account without permission</li>
            <li>Create multiple accounts for fraudulent purposes</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>4. Acceptable Use Policy</h2>
          <p>You agree not to use the Service to:</p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe upon the rights of others</li>
            <li>Transmit any harmful or malicious code</li>
            <li>Attempt to gain unauthorized access to any portion of the Service</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Collect or harvest any personally identifiable information</li>
            <li>Use the Service for any illegal or unauthorized purpose</li>
            <li>Engage in any activity that could damage our reputation</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>5. Payment Terms</h2>
          <h3>5.1 Subscription Fees</h3>
          <p>
            The Service is provided on a subscription basis. You agree to pay all fees according to the pricing 
            plan you select. Fees are:
          </p>
          <ul>
            <li>Billed in advance on a monthly or annual basis</li>
            <li>Non-refundable except as required by law</li>
            <li>Subject to change with 30 days' notice</li>
          </ul>

          <h3>5.2 Payment Processing</h3>
          <p>
            All payments are processed securely through our third-party payment processors. You authorize us to 
            charge your payment method for all fees incurred.
          </p>

          <h3>5.3 Overdue Accounts</h3>
          <p>
            If your account becomes overdue, we may suspend or terminate your access to the Service until payment 
            is received.
          </p>
        </section>

        <section className="terms-section">
          <h2>6. Intellectual Property Rights</h2>
          <h3>6.1 Our Property</h3>
          <p>
            The Service and its original content, features, and functionality are and will remain the exclusive 
            property of BigSystemOne and its licensors. The Service is protected by copyright, trademark, and 
            other laws.
          </p>

          <h3>6.2 Your Content</h3>
          <p>
            You retain ownership of any content you submit to the Service ("User Content"). By submitting User 
            Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, 
            and distribute your content in connection with operating and providing the Service.
          </p>

          <h3>6.3 Feedback</h3>
          <p>
            Any feedback, suggestions, or ideas you provide about the Service may be used by us without any 
            obligation to compensate you.
          </p>
        </section>

        <section className="terms-section">
          <h2>7. Data Processing and Privacy</h2>
          <p>
            Your use of the Service is also governed by our Privacy Policy. By using the Service, you consent 
            to our collection, use, and disclosure of information as described in the Privacy Policy.
          </p>
          <p>
            For restaurant data processing, you acknowledge that:
          </p>
          <ul>
            <li>You are responsible for obtaining necessary consents from your customers</li>
            <li>We act as a data processor on your behalf for certain customer data</li>
            <li>You must comply with applicable data protection laws</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>8. Third-Party Services</h2>
          <p>
            The Service may contain links to or integrate with third-party services that are not owned or 
            controlled by BigSystemOne. We assume no responsibility for the content, privacy policies, or 
            practices of any third-party services.
          </p>
          <p>
            Third-party integrations may include but are not limited to:
          </p>
          <ul>
            <li>Payment processors</li>
            <li>Social media platforms (Facebook, Instagram, etc.)</li>
            <li>Accounting software</li>
            <li>Delivery service platforms</li>
            <li>Marketing and analytics tools</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>9. Service Level Agreement</h2>
          <h3>9.1 Availability</h3>
          <p>
            We strive to maintain 99.9% uptime for the Service. However, we do not guarantee uninterrupted or 
            error-free operation of the Service.
          </p>

          <h3>9.2 Scheduled Maintenance</h3>
          <p>
            We will provide reasonable notice of any scheduled maintenance that may impact Service availability.
          </p>

          <h3>9.3 Support</h3>
          <p>
            Support services are provided according to your subscription plan. Response times and support levels 
            vary by plan.
          </p>
        </section>

        <section className="terms-section">
          <h2>10. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, BIGSYSTEMONE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED 
            DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
          </p>
          <p>
            IN NO EVENT SHALL OUR AGGREGATE LIABILITY EXCEED THE AMOUNTS ACTUALLY PAID BY YOU TO BIGSYSTEMONE IN 
            THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY.
          </p>
        </section>

        <section className="terms-section">
          <h2>11. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless BigSystemOne and its officers, directors, employees, 
            and agents from and against any claims, liabilities, damages, losses, and expenses arising out of or 
            in any way connected with:
          </p>
          <ul>
            <li>Your access to or use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights</li>
            <li>Any User Content you submit</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>12. Disclaimers</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS 
            OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A 
            PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p>
            We do not warrant that:
          </p>
          <ul>
            <li>The Service will meet your specific requirements</li>
            <li>The Service will be uninterrupted, secure, or error-free</li>
            <li>The results obtained from the Service will be accurate or reliable</li>
            <li>Any errors in the Service will be corrected</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>13. Termination</h2>
          <h3>13.1 Termination by You</h3>
          <p>
            You may terminate your account at any time by contacting our support team. Termination does not 
            relieve you of any obligations to pay outstanding fees.
          </p>

          <h3>13.2 Termination by Us</h3>
          <p>
            We may terminate or suspend your account immediately, without prior notice or liability, for any 
            reason, including if you breach these Terms.
          </p>

          <h3>13.3 Effect of Termination</h3>
          <p>
            Upon termination:
          </p>
          <ul>
            <li>Your right to use the Service will immediately cease</li>
            <li>We may delete your account and associated data after 30 days</li>
            <li>You remain liable for any outstanding fees</li>
            <li>All provisions that should survive termination shall remain in effect</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>14. Dispute Resolution</h2>
          <h3>14.1 Arbitration</h3>
          <p>
            Any dispute arising out of or relating to these Terms or the Service shall be resolved through 
            binding arbitration in accordance with the rules of the American Arbitration Association.
          </p>

          <h3>14.2 Class Action Waiver</h3>
          <p>
            You agree that any dispute resolution proceedings will be conducted only on an individual basis and 
            not in a class, consolidated, or representative action.
          </p>
        </section>

        <section className="terms-section">
          <h2>15. General Terms</h2>
          <h3>15.1 Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the United States and 
            the State of Delaware, without regard to its conflict of law provisions.
          </p>

          <h3>15.2 Changes to Terms</h3>
          <p>
            We reserve the right to modify these Terms at any time. We will notify you of any changes by posting 
            the new Terms on this page and updating the "Last Updated" date.
          </p>

          <h3>15.3 Severability</h3>
          <p>
            If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions 
            shall continue in full force and effect.
          </p>

          <h3>15.4 Entire Agreement</h3>
          <p>
            These Terms constitute the entire agreement between you and BigSystemOne regarding the use of the 
            Service, superseding any prior agreements.
          </p>
        </section>

        <section className="terms-section">
          <h2>16. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="contact-info">
            <p><strong>BigSystemOne</strong></p>
            <p>Email: legal@bigsystemone.com</p>
            <p>Address: [Your Business Address]</p>
            <p>Phone: [Your Business Phone]</p>
          </div>
        </section>

        <section className="terms-section">
          <h2>17. Additional Terms for Specific Services</h2>
          <h3>17.1 POS System Usage</h3>
          <p>
            When using our POS system, you agree to:
          </p>
          <ul>
            <li>Ensure all transactions are legitimate and authorized</li>
            <li>Maintain PCI compliance for card processing</li>
            <li>Protect customer payment information</li>
            <li>Report any security incidents immediately</li>
          </ul>

          <h3>17.2 Employee Management</h3>
          <p>
            You are solely responsible for:
          </p>
          <ul>
            <li>Compliance with employment laws and regulations</li>
            <li>Accuracy of employee data and scheduling</li>
            <li>Proper use of employee monitoring features</li>
          </ul>

          <h3>17.3 Social Media Integration</h3>
          <p>
            When using our social media features, you must:
          </p>
          <ul>
            <li>Comply with each platform's terms of service</li>
            <li>Obtain necessary rights for any content posted</li>
            <li>Not engage in spam or unauthorized marketing</li>
          </ul>
        </section>
      </div>
    </div>
  );
};